/**
 * Booking visual palette + small primitives.
 *
 * The CSS in this project uses an inverted naming convention: the JS flag
 * `useTheme().isDarkMode` is the *opposite* of the visually-dark mode. To
 * keep the rest of the booking code easy to read we always work with
 * `isVisuallyDark = !isDarkMode` and pick palette tokens from here.
 */

export function getBookingPalette(isVisuallyDark) {
  if (isVisuallyDark) {
    return {
      pageBg: "transparent",
      surface: "#191C1F",
      surfaceAlt: "#191C1F",
      surfaceHover: "#23272B",
      serviceSelectedBg: "#162E2C",
      serviceSelectedBorder: "#089474",
      serviceTimeGroupBg: "rgba(255, 255, 255, 0.1)",
      serviceTimeBg: "rgba(17, 19, 21, 0.4)",
      serviceTimeBorder: "#141522",
      border: "1px solid rgba(255, 255, 255, 0.12)",
      borderStrong: "1px solid rgba(255, 255, 255, 0.2)",
      text: "hsl(0 0% 96%)",
      textMuted: "hsl(0 0% 75%)",
      textSubtle: "hsl(0 0% 60%)",
      accent: "hsl(166 80% 55%)",
      accentSoft: "hsla(166, 80%, 55%, 0.16)",
      accentBorder: "hsl(166 75% 50%)",
      ratingButtonBg: "#11131566",
      emptyStar: "#FFFFFF",
      shadow: "0 8px 24px rgba(0, 0, 0, 0.35)",
      shadowSoft: "0 2px 10px rgba(0, 0, 0, 0.25)",
      progressTrack: "#202428",
      progressBar:
        "linear-gradient(90deg, hsl(166 80% 55%), hsl(184 75% 55%))",
      stepDoneBg: "hsl(166 70% 45%)",
      stepActiveBg: "hsl(166 80% 55%)",
      stepIdleBg: "#202428",
      stepIdleText: "hsl(0 0% 65%)",
      stepActiveText: "#0b1020",
      stepDoneText: "#ffffff",
      stepLabelActive: "hsl(0 0% 96%)",
      stepLabelIdle: "hsl(0 0% 65%)",
    };
  }
  return {
    pageBg: "transparent",
    surface: "hsl(0 0% 100%)",
    surfaceAlt: "hsl(220 30% 98%)",
    surfaceHover: "hsl(220 25% 96%)",
    serviceSelectedBg: "#E5F7F2",
    serviceBg: "#F0F1F9",
    specialistBg: "#F0F1F9",
    serviceSelectedBorder: "#009474",
    serviceTimeGroupBg: "rgba(17, 19, 21, 0.1)",
    serviceTimeBg: "#D6D9DE",
    serviceTimeBorder: "#B4B6C0",
    border: "1px solid hsl(220 20% 92%)",
    borderStrong: "1px solid hsl(220 25% 82%)",
    text: "hsl(220 30% 18%)",
    textMuted: "hsl(220 15% 38%)",
    textSubtle: "hsl(220 12% 55%)",
    accent: "hsl(166 100% 32%)",
    accentSoft: "hsla(166, 100%, 41%, 0.10)",
    accentBorder: "hsl(166 100% 38%)",
    ratingButtonBg: "#D6D9DE",
    emptyStar: "#8B919B",
    shadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
    shadowSoft: "0 2px 10px rgba(15, 23, 42, 0.06)",
    progressTrack: "hsl(220 20% 90%)",
    progressBar:
      "linear-gradient(90deg, hsl(166 100% 38%), hsl(184 70% 45%))",
    stepDoneBg: "hsl(166 80% 38%)",
    stepActiveBg: "hsl(166 100% 38%)",
    stepIdleBg: "hsl(220 20% 92%)",
    stepIdleText: "hsl(220 15% 50%)",
    stepActiveText: "#ffffff",
    stepDoneText: "#ffffff",
    stepLabelActive: "hsl(220 30% 18%)",
    stepLabelIdle: "hsl(220 15% 50%)",
  };
}

/** Maps a backend storage path (e.g. "/storage/...") to an absolute URL. */
export function resolveImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_SECOND_API_KEY || "").replace(/\/+$/, "");
  const rel = path.startsWith("/") ? path : `/${path}`;
  return `${base}${rel}`;
}
